import type { ApiMessage, ApiMessageSearchType, ApiPrivacySettings, ApiUsername } from '../../../api/types';
import type { TrackId } from '../../../util/audioController';
import type { ActionReturnType } from '../../types';
import { MAIN_THREAD_ID } from '../../../api/types';
import {
  AudioOrigin,
  ProfileEditProgress,
  SettingsScreens,
  UPLOADING_WALLPAPER_SLUG,
} from '../../../types';

import {
  APP_CONFIG_REFETCH_INTERVAL,
  COUNTRIES_WITH_12H_TIME_FORMAT,
  MUTE_INDEFINITE_TIMESTAMP,
  UNMUTE_TIMESTAMP,
} from '../../../config';
import { aiAuthHeaders, aiBackendBase, handleAiUnauthorized } from '../../../util/aiAuth';
import { audioController, YOUTUBE_CHAT_ID } from '../../../util/audioController';
import { toCredentialCreationOptions } from '../../../util/browser/passkeys';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import { buildCollectionByKey } from '../../../util/iteratees';
import { requestPermission, subscribe, unsubscribe } from '../../../util/notifications';
import { setTimeFormat } from '../../../util/oldLangProvider';
import requestActionTimeout from '../../../util/requestActionTimeout';
import { getServerTime } from '../../../util/serverTime';
import { callApi } from '../../../api/gramjs';
import { buildApiInputPrivacyRules } from '../../helpers';
import { addActionHandler, getActions, getGlobal, getPromiseActions, setGlobal } from '../../index';
import {
  addBlockedUser, addChatMessagesById, addNotifyExceptions, deletePeerPhoto,
  removeBlockedUser, replaceSettings, updateChat,
  updateNotifyDefaults, updateSharedSettings, updateUser, updateUserFullInfo,
} from '../../reducers';
import { updateTabState } from '../../reducers/tabs';
import {
  selectChat, selectIsCurrentUserFrozen,
  selectTabState, selectUser,
} from '../../selectors';
import { selectPeer } from '../../selectors/peers';
import { selectSharedSettings } from '../../selectors/sharedState';

addActionHandler('updateProfile', async (global, actions, payload): Promise<void> => {
  const {
    photo, firstName, lastName, bio: about, username,
    tabId = getCurrentTabId(),
  } = payload;

  const { currentUserId } = global;
  if (!currentUserId) {
    return;
  }

  global = updateTabState(global, {
    profileEdit: {
      progress: ProfileEditProgress.InProgress,
    },
  }, tabId);
  setGlobal(global);

  if (photo) {
    await callApi('uploadProfilePhoto', photo);
  }

  if (firstName || lastName || about) {
    const result = await callApi('updateProfile', { firstName, lastName, about });
    if (result) {
      global = getGlobal();
      const currentUser = currentUserId && selectUser(global, currentUserId);

      if (currentUser) {
        global = updateUser(
          global,
          currentUser.id,
          {
            firstName,
            lastName,
          },
        );
        global = updateUserFullInfo(global, currentUser.id, { bio: about });
        setGlobal(global);
      }
    }
  }

  if (username !== undefined) {
    const result = await callApi('updateUsername', username);
    global = getGlobal();
    const currentUser = currentUserId && selectUser(global, currentUserId);

    if (result && currentUser) {
      const shouldUsernameUpdate = currentUser.usernames?.find((u) => u.isEditable);
      const usernames = shouldUsernameUpdate
        ? currentUser.usernames?.map((u) => (u.isEditable ? { ...u, username } : u))
        : [{ username, isEditable: true, isActive: true } as ApiUsername, ...currentUser.usernames || []];
      global = updateUser(global, currentUserId, { usernames });
      setGlobal(global);
    }
  }

  global = getGlobal();
  global = updateTabState(global, {
    profileEdit: {
      progress: ProfileEditProgress.Complete,
    },
  }, tabId);
  setGlobal(global);

  if (photo) {
    actions.loadFullUser({ userId: currentUserId, withPhotos: true });
  }
});

addActionHandler('updateBirthday', async (global, actions, payload): Promise<void> => {
  const { birthday } = payload;
  const { currentUserId } = global;
  if (!currentUserId) return;

  const result = await callApi('updateBirthday', birthday);
  if (!result) return;

  actions.loadFullUser({ userId: currentUserId });
});

addActionHandler('updateProfilePhoto', async (global, actions, payload): Promise<void> => {
  const { photo, isFallback } = payload;
  const { currentUserId } = global;
  if (!currentUserId) return;
  const currentUser = selectUser(global, currentUserId);
  if (!currentUser) return;

  global = updateUser(global, currentUserId, { avatarPhotoId: undefined });
  global = updateUserFullInfo(global, currentUserId, { profilePhoto: undefined });

  setGlobal(global);

  const result = await callApi('updateProfilePhoto', photo, isFallback);
  if (!result) return;

  actions.loadFullUser({ userId: currentUserId, withPhotos: true });
});

addActionHandler('deleteProfilePhoto', async (global, actions, payload): Promise<void> => {
  const { photo } = payload;
  const { currentUserId } = global;
  if (!currentUserId) return;

  const isDeleted = await callApi('deleteProfilePhotos', [photo]);
  if (!isDeleted) return;

  global = getGlobal();
  global = deletePeerPhoto(global, currentUserId, photo.id);
  setGlobal(global);

  actions.loadFullUser({ userId: currentUserId, withPhotos: true });
});

addActionHandler('checkUsername', async (global, actions, payload): Promise<void> => {
  const { username, tabId = getCurrentTabId() } = payload;

  let tabState = selectTabState(global, tabId);
  // No need to check the username if profile update is already in progress
  if (tabState.profileEdit && tabState.profileEdit.progress === ProfileEditProgress.InProgress) {
    return;
  }

  global = updateTabState(global, {
    profileEdit: {
      progress: tabState.profileEdit ? tabState.profileEdit.progress : ProfileEditProgress.Idle,
      checkedUsername: undefined,
      isUsernameAvailable: undefined,
      error: undefined,
    },
  }, tabId);
  setGlobal(global);

  const { result, error } = (await callApi('checkUsername', username));

  global = getGlobal();
  tabState = selectTabState(global, tabId);
  global = updateTabState(global, {
    profileEdit: {
      ...tabState.profileEdit!,
      checkedUsername: username,
      isUsernameAvailable: result === true,
      error,
    },
  }, tabId);
  setGlobal(global);
});

addActionHandler('loadWallpapers', async (global): Promise<void> => {
  const result = await callApi('fetchWallpapers');
  if (!result) {
    return;
  }

  global = getGlobal();
  global = {
    ...global,
    settings: {
      ...global.settings,
      loadedWallpapers: result.wallpapers,
    },
  };
  setGlobal(global);
});

addActionHandler('uploadWallpaper', async (global, actions, payload): Promise<void> => {
  const file = payload;
  const previewBlobUrl = URL.createObjectURL(file);

  global = {
    ...global,
    settings: {
      ...global.settings,
      loadedWallpapers: [
        {
          slug: UPLOADING_WALLPAPER_SLUG,
          document: {
            mediaType: 'document',
            fileName: '',
            size: file.size,
            mimeType: file.type,
            previewBlobUrl,
          },
        },
        ...(global.settings.loadedWallpapers || []),
      ],
    },
  };
  setGlobal(global);

  const result = await callApi('uploadWallpaper', file);
  if (!result) {
    return;
  }

  const { wallpaper } = result;

  global = getGlobal();
  if (!global.settings.loadedWallpapers) {
    return;
  }

  const firstWallpaper = global.settings.loadedWallpapers[0];
  if (!firstWallpaper || firstWallpaper.slug !== UPLOADING_WALLPAPER_SLUG) {
    return;
  }

  const withLocalMedia = {
    ...wallpaper,
    document: {
      ...wallpaper.document,
      previewBlobUrl,
    },
  };

  global = {
    ...global,
    settings: {
      ...global.settings,
      loadedWallpapers: [
        withLocalMedia,
        ...global.settings.loadedWallpapers.slice(1),
      ],
    },
  };
  setGlobal(global);
});

addActionHandler('loadBlockedUsers', async (global): Promise<void> => {
  const result = await callApi('fetchBlockedUsers', {});
  if (!result) return;

  global = getGlobal();

  global = {
    ...global,
    blocked: {
      ids: result.blockedIds,
      totalCount: result.totalCount,
    },
  };

  setGlobal(global);
});

addActionHandler('blockUser', async (global, actions, payload): Promise<void> => {
  const { userId, isOnlyStories } = payload;

  const user = selectUser(global, userId);
  if (!user) return;

  const result = await callApi('blockUser', {
    user,
    isOnlyStories: isOnlyStories || undefined,
  });
  if (!result) return;

  global = getGlobal();
  global = addBlockedUser(global, userId);
  setGlobal(global);
});

addActionHandler('unblockUser', async (global, actions, payload): Promise<void> => {
  const { userId, isOnlyStories } = payload;

  const user = selectUser(global, userId);
  if (!user) return;

  const result = await callApi('unblockUser', {
    user,
    isOnlyStories: isOnlyStories || undefined,
  });
  if (!result) return;

  global = getGlobal();
  global = removeBlockedUser(global, userId);
  setGlobal(global);
});

addActionHandler('loadNotificationExceptions', async (global): Promise<void> => {
  const result = await callApi('fetchNotificationExceptions');
  if (!result) {
    return;
  }

  global = getGlobal();
  global = addNotifyExceptions(global, result);
  setGlobal(global);
});

addActionHandler('loadNotificationSettings', async (global): Promise<void> => {
  const [signUpNotification, notifyDefaults] = await Promise.all([
    callApi('fetchContactSignUpSetting'),
    callApi('fetchNotifyDefaultSettings'),
  ]);

  if (!notifyDefaults) return;

  global = getGlobal();
  global = replaceSettings(global, {
    hasContactJoinedNotifications: signUpNotification,
  });
  global = {
    ...global,
    settings: {
      ...global.settings,
      notifyDefaults,
    },
  };
  setGlobal(global);
});

addActionHandler('updateNotificationSettings', async (global, actions, payload): Promise<void> => {
  const { peerType, isMuted, shouldShowPreviews } = payload;

  const result = await callApi('updateNotificationSettings', peerType, { isMuted, shouldShowPreviews });
  if (!result) {
    return;
  }

  global = getGlobal();
  global = updateNotifyDefaults(global, peerType, {
    mutedUntil: isMuted ? MUTE_INDEFINITE_TIMESTAMP : UNMUTE_TIMESTAMP,
    shouldShowPreviews,
  });
  setGlobal(global);
});

addActionHandler('updateWebNotificationSettings', async (global, actions, payload): Promise<void> => {
  const oldSettings = global.settings.byKey;
  global = replaceSettings(global, payload);
  setGlobal(global);
  const { hasWebNotifications, hasPushNotifications } = global.settings.byKey;
  if (!oldSettings.hasPushNotifications && hasPushNotifications) {
    await subscribe();
  }
  if (oldSettings.hasPushNotifications && !hasPushNotifications) {
    await unsubscribe();
  }
  if (!oldSettings.hasWebNotifications && hasWebNotifications) {
    const isGranted = await requestPermission();
    if (!isGranted) {
      global = getGlobal();
      global = replaceSettings(global, { hasWebNotifications: false });
      setGlobal(global);
    }
  }
});

addActionHandler('updateContactSignUpNotification', async (global, actions, payload): Promise<void> => {
  const { isSilent } = payload;

  const result = await callApi('updateContactSignUpNotification', isSilent);
  if (!result) {
    return;
  }

  global = getGlobal();
  global = replaceSettings(global, { hasContactJoinedNotifications: !isSilent });
  setGlobal(global);
});

addActionHandler('loadLanguages', async (global): Promise<void> => {
  const result = await callApi('fetchLanguages');
  if (!result) {
    return;
  }

  global = getGlobal();
  global = updateSharedSettings(global, { languages: result });
  setGlobal(global);
});

addActionHandler('loadPrivacySettings', async (global, actions, payload): Promise<void> => {
  const { skipIfCached } = payload;
  if (skipIfCached && Object.keys(global.settings.privacy).length > 0) {
    return;
  }

  if (selectIsCurrentUserFrozen(global)) return;

  const result = await Promise.all([
    callApi('fetchPrivacySettings', 'phoneNumber'),
    callApi('fetchPrivacySettings', 'addByPhone'),
    callApi('fetchPrivacySettings', 'lastSeen'),
    callApi('fetchPrivacySettings', 'profilePhoto'),
    callApi('fetchPrivacySettings', 'forwards'),
    callApi('fetchPrivacySettings', 'chatInvite'),
    callApi('fetchPrivacySettings', 'phoneCall'),
    callApi('fetchPrivacySettings', 'phoneP2P'),
    callApi('fetchPrivacySettings', 'voiceMessages'),
    callApi('fetchPrivacySettings', 'bio'),
    callApi('fetchPrivacySettings', 'birthday'),
    callApi('fetchPrivacySettings', 'gifts'),
    callApi('fetchPrivacySettings', 'noPaidMessages'),
  ]);

  if (result.some((e) => e === undefined)) {
    return;
  }

  const [
    phoneNumberSettings,
    addByPhoneSettings,
    lastSeenSettings,
    profilePhotoSettings,
    forwardsSettings,
    chatInviteSettings,
    phoneCallSettings,
    phoneP2PSettings,
    voiceMessagesSettings,
    bioSettings,
    birthdaySettings,
    giftsSettings,
    noPaidMessagesSettings,
  ] = result as {
    rules: ApiPrivacySettings;
  }[];

  global = getGlobal();
  global = {
    ...global,
    settings: {
      ...global.settings,
      privacy: {
        ...global.settings.privacy,
        phoneNumber: phoneNumberSettings.rules,
        addByPhone: addByPhoneSettings.rules,
        lastSeen: lastSeenSettings.rules,
        profilePhoto: profilePhotoSettings.rules,
        forwards: forwardsSettings.rules,
        chatInvite: chatInviteSettings.rules,
        phoneCall: phoneCallSettings.rules,
        phoneP2P: phoneP2PSettings.rules,
        voiceMessages: voiceMessagesSettings.rules,
        bio: bioSettings.rules,
        birthday: birthdaySettings.rules,
        gifts: giftsSettings.rules,
        noPaidMessages: noPaidMessagesSettings.rules,
      },
    },
  };
  setGlobal(global);
});

addActionHandler('setPrivacyVisibility', async (global, actions, payload): Promise<void> => {
  const { privacyKey, visibility, onSuccess } = payload;

  if (!global.settings.privacy[privacyKey]) {
    const result = await callApi('fetchPrivacySettings', privacyKey);
    if (!result) {
      return;
    }

    global = getGlobal();
    global = {
      ...global,
      settings: {
        ...global.settings,
        privacy: {
          ...global.settings.privacy,
          [privacyKey]: result.rules,
        },
      },
    };
    setGlobal(global);
  }

  const {
    privacy: { [privacyKey]: settings },
  } = global.settings;

  if (!settings) {
    return;
  }

  const rules = buildApiInputPrivacyRules(global, {
    visibility,
    allowedIds: [...settings.allowUserIds, ...settings.allowChatIds],
    blockedIds: [...settings.blockUserIds, ...settings.blockChatIds],
    botsPrivacy: settings.botsPrivacy,
  });

  const result = await callApi('setPrivacySettings', privacyKey, rules);
  if (!result) {
    return;
  }

  onSuccess?.();

  global = getGlobal();
  global = {
    ...global,
    settings: {
      ...global.settings,
      privacy: {
        ...global.settings.privacy,
        [privacyKey]: result.rules,
      },
    },
  };
  setGlobal(global);
});

addActionHandler('setPrivacySettings', async (global, actions, payload): Promise<void> => {
  const {
    privacyKey, isAllowList, updatedIds, isPremiumAllowed, botsPrivacy,
  } = payload;
  const {
    privacy: { [privacyKey]: settings },
  } = global.settings;

  if (!settings) {
    return;
  }

  if (privacyKey === 'noPaidMessages') {
    global = getGlobal();
    const idsForUpdate = [
      ...updatedIds.filter((id) => !settings.allowUserIds.includes(id)),
      ...settings.allowUserIds.filter((id) => !updatedIds.includes(id)),
    ];

    idsForUpdate.forEach((userId) => {
      global = updateUserFullInfo(global, userId, {
        settings: undefined,
      });
    });
    setGlobal(global);
  }

  const rules = buildApiInputPrivacyRules(global, {
    visibility: settings.visibility,
    isUnspecified: settings.isUnspecified,
    shouldAllowPremium: isPremiumAllowed,
    allowedIds: isAllowList ? updatedIds : [...settings.allowUserIds, ...settings.allowChatIds],
    blockedIds: !isAllowList ? updatedIds : [...settings.blockUserIds, ...settings.blockChatIds],
    botsPrivacy,
  });

  const result = await callApi('setPrivacySettings', privacyKey, rules);
  if (!result) {
    return;
  }

  global = getGlobal();
  global = {
    ...global,
    settings: {
      ...global.settings,
      privacy: {
        ...global.settings.privacy,
        [privacyKey]: result.rules,
      },
    },
  };
  setGlobal(global);
});

addActionHandler('updateIsOnline', (global, actions, payload): ActionReturnType => {
  if (global.connectionState !== 'connectionStateReady') return;
  callApi('updateIsOnline', payload.isOnline);
});

addActionHandler('loadContentSettings', async (global): Promise<void> => {
  if (selectIsCurrentUserFrozen(global)) return;

  const result = await callApi('fetchContentSettings');
  if (!result) return;

  global = getGlobal();
  global = replaceSettings(global, result);
  setGlobal(global);
});

addActionHandler('updateContentSettings', async (global, actions, payload): Promise<void> => {
  global = replaceSettings(global, { isSensitiveEnabled: payload.isSensitiveEnabled });
  setGlobal(global);

  const result = await callApi('updateContentSettings', payload.isSensitiveEnabled);
  if (!result) {
    global = getGlobal();
    global = replaceSettings(global, { isSensitiveEnabled: !payload.isSensitiveEnabled });
    setGlobal(global);
  }
});

addActionHandler('loadCountryList', async (global, actions, payload): Promise<void> => {
  let { langCode } = payload;
  if (!langCode) langCode = selectSharedSettings(global).language;

  const countryList = await callApi('fetchCountryList', { langCode });
  if (!countryList) return;

  global = getGlobal();
  global = {
    ...global,
    countryList,
  };
  setGlobal(global);
});

addActionHandler('ensureTimeFormat', async (global, actions): Promise<void> => {
  if (global.auth.nearestCountry) {
    const timeFormat = COUNTRIES_WITH_12H_TIME_FORMAT
      .has(global.auth.nearestCountry.toUpperCase()) ? '12h' : '24h';
    actions.setSharedSettingOption({ timeFormat });
    setTimeFormat(timeFormat);
  }

  if (selectSharedSettings(global).wasTimeFormatSetManually) {
    return;
  }

  const nearestCountryCode = await callApi('fetchNearestCountry');
  if (nearestCountryCode) {
    const timeFormat = COUNTRIES_WITH_12H_TIME_FORMAT.has(nearestCountryCode.toUpperCase()) ? '12h' : '24h';
    actions.setSharedSettingOption({ timeFormat });
    setTimeFormat(timeFormat);
  }
});

addActionHandler('loadAppConfig', async (global, actions, payload): Promise<void> => {
  const hash = payload?.hash;

  const appConfig = await callApi('fetchAppConfig', { hash });
  if (!appConfig) return;

  requestActionTimeout({
    action: 'loadAppConfig',
    payload: { hash: appConfig.hash },
  }, APP_CONFIG_REFETCH_INTERVAL);

  global = getGlobal();
  global = {
    ...global,
    appConfig,
    isAppConfigLoaded: true,
  };
  setGlobal(global);
});

addActionHandler('loadConfig', async (global): Promise<void> => {
  const config = await callApi('fetchConfig');
  if (!config) return;

  global = getGlobal();
  const timeout = config.expiresAt - getServerTime();
  requestActionTimeout({
    action: 'loadConfig',
    payload: undefined,
  }, timeout * 1000);

  global = {
    ...global,
    config,
  };
  setGlobal(global);
});

addActionHandler('loadPromoData', async (global): Promise<void> => {
  const promoData = await callApi('fetchPromoData');

  global = getGlobal();
  const timeout = (promoData?.expires || 0) - getServerTime();
  if (timeout > 0) {
    requestActionTimeout({
      action: 'loadPromoData',
      payload: undefined,
    }, timeout * 1000);
  }

  global = {
    ...global,
    promoData,
  };
  setGlobal(global);
});

addActionHandler('dismissSuggestion', async (global, actions, payload): Promise<void> => {
  const { suggestion } = payload;
  await callApi('dismissSuggestion', suggestion);

  actions.loadPromoData();
});

addActionHandler('loadPeerColors', async (global): Promise<void> => {
  const generalHash = global.peerColors?.generalHash;
  const profileHash = global.peerColors?.profileHash;
  const [generalResult, profileResult] = await Promise.all([
    callApi('fetchPeerColors', generalHash),
    callApi('fetchPeerProfileColors', profileHash),
  ]);

  if (!generalResult && !profileResult) return;

  global = getGlobal();

  const currentPeerColors = global.peerColors! || {};

  global = {
    ...global,
    peerColors: {
      ...currentPeerColors,
      general: generalResult?.colors || currentPeerColors.general,
      generalHash: generalResult?.hash || currentPeerColors.generalHash,
      profile: profileResult?.colors || currentPeerColors.profile,
      profileHash: profileResult?.hash || currentPeerColors.profileHash,
    },
  };
  setGlobal(global);
});

addActionHandler('loadTimezones', async (global): Promise<void> => {
  const hash = global.timezones?.hash;
  const result = await callApi('fetchTimezones', hash);
  if (!result) return;

  global = getGlobal();
  global = {
    ...global,
    timezones: {
      byId: buildCollectionByKey(result.timezones, 'id'),
      hash: result.hash,
    },
  };
  setGlobal(global);
});

addActionHandler('loadGlobalPrivacySettings', async (global): Promise<void> => {
  const globalSettings = await callApi('fetchGlobalPrivacySettings');
  if (!globalSettings) {
    return;
  }

  global = getGlobal();
  global = replaceSettings(global, { ...globalSettings });
  setGlobal(global);
});

addActionHandler('updateGlobalPrivacySettings', async (global, actions, payload): Promise<void> => {
  const shouldArchiveAndMuteNewNonContact = payload.shouldArchiveAndMuteNewNonContact
    ?? Boolean(global.settings.byKey.shouldArchiveAndMuteNewNonContact);
  const shouldHideReadMarks = payload.shouldHideReadMarks ?? Boolean(global.settings.byKey.shouldHideReadMarks);
  const shouldNewNonContactPeersRequirePremium = payload.shouldNewNonContactPeersRequirePremium
    ?? Boolean(global.settings.byKey.shouldNewNonContactPeersRequirePremium);
    // eslint-disable-next-line no-null/no-null
  const nonContactPeersPaidStars = payload.nonContactPeersPaidStars === null ? undefined
    : payload.nonContactPeersPaidStars || global.settings.byKey.nonContactPeersPaidStars;
  const shouldDisplayGiftsButton = payload.shouldDisplayGiftsButton
    ?? Boolean(global.settings.byKey.shouldDisplayGiftsButton);
  const disallowedGifts = payload.disallowedGifts
    ?? global.settings.byKey.disallowedGifts;

  // eslint-disable-next-line no-null/no-null
  const shouldUpdateUsersSettings = (payload.nonContactPeersPaidStars === null)
    || payload.nonContactPeersPaidStars;

  global = getGlobal();
  global = replaceSettings(global, {
    shouldArchiveAndMuteNewNonContact,
    shouldHideReadMarks,
    shouldNewNonContactPeersRequirePremium,
    nonContactPeersPaidStars,
    shouldDisplayGiftsButton,
    disallowedGifts,
  });
  setGlobal(global);

  const result = await callApi('updateGlobalPrivacySettings', {
    shouldArchiveAndMuteNewNonContact,
    shouldHideReadMarks,
    shouldNewNonContactPeersRequirePremium,
    nonContactPeersPaidStars,
    shouldDisplayGiftsButton,
    disallowedGifts,
  });

  global = getGlobal();
  global = replaceSettings(global, {
    shouldArchiveAndMuteNewNonContact: !result
      ? !shouldArchiveAndMuteNewNonContact
      : result.shouldArchiveAndMuteNewNonContact,
    shouldHideReadMarks: !result ? !shouldHideReadMarks : result.shouldHideReadMarks,
    shouldNewNonContactPeersRequirePremium: !result
      ? !shouldNewNonContactPeersRequirePremium
      : result.shouldNewNonContactPeersRequirePremium,
    nonContactPeersPaidStars: !result
      ? undefined
      : result.nonContactPeersPaidStars,
    shouldDisplayGiftsButton: !result ? !shouldDisplayGiftsButton : result.shouldDisplayGiftsButton,
    disallowedGifts: !result ? disallowedGifts : result.disallowedGifts,
  });

  if (shouldUpdateUsersSettings) {
    Object.keys(global.users.fullInfoById).forEach((userId) => {
      global = updateUserFullInfo(global, userId, {
        settings: undefined,
      });
    });
  }

  setGlobal(global);
});

addActionHandler('toggleUsername', async (global, actions, payload): Promise<void> => {
  const { username, isActive } = payload;
  const { currentUserId } = global;
  if (!currentUserId) {
    return;
  }

  const currentUser = selectUser(global, currentUserId);
  if (!currentUser?.usernames) {
    return;
  }

  const usernames = currentUser.usernames.map((item) => {
    if (item.username !== username) {
      return item;
    }

    return { ...item, isActive: isActive || undefined };
  });

  global = updateUser(global, currentUserId, { usernames });
  setGlobal(global);

  const result = await callApi('toggleUsername', { username, isActive });

  if (!result) {
    actions.loadFullUser({ userId: currentUserId });
  }
});

addActionHandler('toggleChatUsername', async (global, actions, payload): Promise<void> => {
  const {
    chatId, username, isActive,
  } = payload;
  const chat = selectChat(global, chatId);
  if (!chat?.usernames) {
    return;
  }

  const usernames = chat.usernames.map((item) => {
    if (item.username !== username) {
      return item;
    }

    return { ...item, isActive: isActive || undefined };
  });

  global = updateChat(global, chatId, { usernames });
  setGlobal(global);

  const result = await callApi('toggleUsername', {
    chatId: chat.id,
    accessHash: chat.accessHash,
    username,
    isActive,
  });

  if (!result) {
    actions.loadFullChat({ chatId });
  }
});

addActionHandler('sortUsernames', async (global, actions, payload): Promise<void> => {
  const { usernames } = payload;
  const { currentUserId } = global;
  if (!currentUserId) {
    return;
  }

  const result = await callApi('reorderUsernames', { usernames });

  // After saving the order of usernames, server sends an update with the necessary data,
  // so there is no need to update the state in this place
  if (!result) {
    actions.loadUser({ userId: currentUserId });
  }
});

addActionHandler('sortChatUsernames', async (global, actions, payload): Promise<void> => {
  const { chatId, usernames } = payload;
  const chat = selectChat(global, chatId);
  if (!chat) {
    return;
  }

  const prevUsernames = [...chat.usernames!];
  const sortedUsernames = chat.usernames!.reduce((res, currentUsername) => {
    const idx = usernames.findIndex((username) => username === currentUsername.username);
    res[idx] = currentUsername;

    return res;
  }, [] as ApiUsername[]);

  global = updateChat(global, chatId, { usernames: sortedUsernames });
  setGlobal(global);

  const result = await callApi('reorderUsernames', {
    chatId: chat.id,
    accessHash: chat.accessHash,
    usernames,
  });

  if (!result) {
    global = getGlobal();
    global = updateChat(global, chatId, { usernames: prevUsernames });
    setGlobal(global);
  }
});

addActionHandler('loadPasskeys', async (global): Promise<void> => {
  const result = await callApi('fetchPasskeys');
  if (!result) {
    global = getGlobal();
    global = {
      ...global,
      settings: {
        ...global.settings,
        passkeys: undefined,
      },
    };
    setGlobal(global);
    return;
  }

  global = getGlobal();
  global = {
    ...global,
    settings: {
      ...global.settings,
      passkeys: result.passkeys,
    },
  };
  setGlobal(global);
});

addActionHandler('startPasskeyRegistration', async (global, actions, payload): Promise<void> => {
  const { tabId = getCurrentTabId() } = payload || {};

  const preparedOptions = await callApi('initPasskeyRegistration');
  if (!preparedOptions) return;

  const options = toCredentialCreationOptions(preparedOptions);
  const credential = await navigator.credentials.create(options).catch((e: unknown) => {
    if (e instanceof DOMException && e.name === 'NotAllowedError') {
      actions.showNotification({
        message: {
          key: 'PasskeyCreateError',
        },
        tabId,
      });
      return undefined;
    }
    throw e;
  });
  if (!credential) return;
  const publicKeyCredential = credential as PublicKeyCredential;

  const result = await callApi('registerPasskey', publicKeyCredential.toJSON() as RegistrationResponseJSON);
  if (!result) return;

  await getPromiseActions().loadPasskeys();
  actions.openSettingsScreen({ screen: SettingsScreens.Passkeys, tabId });
});

addActionHandler('deletePasskey', async (global, actions, payload): Promise<void> => {
  const { id } = payload;

  const passkeys = global.settings.passkeys;
  if (passkeys?.length) {
    const filteredPasskeys = passkeys.filter((passkey) => passkey.id !== id);
    global = {
      ...global,
      settings: {
        ...global.settings,
        passkeys: filteredPasskeys,
      },
    };
    setGlobal(global);
  }

  await callApi('deletePasskey', { id });

  actions.loadPasskeys();
});

// AI Actions
addActionHandler('closeAiPanel', (global, _actions, payload): ActionReturnType => {
  const { tabId = getCurrentTabId() } = payload || {};
  return updateTabState(global, { aiPanel: undefined }, tabId);
});

addActionHandler('requestFactCheck', async (global, actions, payload): Promise<void> => {
  const { chatId, messageId, tabId = getCurrentTabId() } = payload;
  const {
    aiApiKey, aiProvider, aiLanguage,
  } = selectSharedSettings(global);
  const aiBackendUrl = aiBackendBase();

  if (aiProvider !== 'claude-cli' && !aiApiKey) {
    actions.showNotification({ message: { key: 'AiNoApiKey' }, tabId });
    return;
  }

  const message = global.messages.byChatId[chatId]?.byId[messageId];
  if (!message) return;

  const { text, photo, video } = message.content;
  const hasMedia = Boolean(photo || video);
  const messageText = text?.text;

  if (!messageText && !hasMedia) return;

  if (!messageText) {
    if (hasMedia) {
      actions.showNotification({
        message: { key: 'AiFactCheckMediaOnly' },
        tabId,
      });
    }
    return;
  }

  const resultKey = `${chatId}_${messageId}`;

  // Check localStorage cache
  try {
    const cacheRaw = localStorage.getItem('ai_factcheck_cache');
    if (cacheRaw) {
      const cache = JSON.parse(cacheRaw);
      const cached = cache[resultKey];
      if (cached && Date.now() - cached.ts < 7 * 24 * 60 * 60 * 1000) {
        global = getGlobal();
        const cachedResults = selectTabState(global, tabId).factCheckResults || {};
        global = updateTabState(global, {
          factCheckResults: { ...cachedResults, [resultKey]: cached.data },
        }, tabId);
        setGlobal(global);
        return;
      }
    }
  } catch { /* ignore cache errors */ }

  // Set loading state
  global = getGlobal();
  const currentResults = selectTabState(global, tabId).factCheckResults || {};
  global = updateTabState(global, {
    factCheckResults: { ...currentResults, [resultKey]: { isLoading: true } },
  }, tabId);
  setGlobal(global);

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Provider': aiProvider,
      ...aiAuthHeaders(),
    };
    if (aiApiKey) {
      headers.Authorization = `Bearer ${aiApiKey}`;
    }

    // Include message date for better context
    const msgDate = message.date ? new Date(message.date * 1000).toISOString().split('T')[0] : '';
    const textWithDate = msgDate
      ? `[Message posted on ${msgDate}]\n\n${messageText}`
      : messageText;

    const response = await fetch(`${aiBackendUrl}/api/fact-check`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ text: textWithDate, language: aiLanguage || 'ru' }),
    });
    handleAiUnauthorized(response.status);

    const result = response.ok ? await response.json() : undefined;
    global = getGlobal();
    const results = selectTabState(global, tabId).factCheckResults || {};

    if (result) {
      const resultData = {
        verdict: result.verdict,
        summary: result.summary,
        claims: result.claims,
        sources: result.sources,
      };

      // Save to localStorage cache
      try {
        const cacheRaw2 = localStorage.getItem('ai_factcheck_cache');
        const cache2 = cacheRaw2 ? JSON.parse(cacheRaw2) : {};
        cache2[resultKey] = { data: resultData, ts: Date.now() };
        // Keep max 50 entries
        const keys = Object.keys(cache2);
        if (keys.length > 50) {
          const sorted = keys.sort((a, b) => (cache2[a].ts || 0) - (cache2[b].ts || 0));
          for (let i = 0; i < keys.length - 50; i++) delete cache2[sorted[i]];
        }
        localStorage.setItem('ai_factcheck_cache', JSON.stringify(cache2));
      } catch { /* ignore */ }

      global = updateTabState(global, {
        factCheckResults: {
          ...results,
          [resultKey]: resultData,
        },
      }, tabId);
    } else if (response.status === 429) {
      let errorMsg = 'Weekly limit reached.';
      try {
        const errData = await response.json();
        if (errData.detail?.upgrade_url) {
          errorMsg = `${errData.detail.message} `;
          global = getGlobal();
          const aiBotUsername = selectSharedSettings(global).aiBotUsername || 'MeretricesBot';
          actions.openChatByUsername({ username: aiBotUsername, startParam: 'ref_limit', tabId });
        }
      } catch { /* ignore */ }
      global = updateTabState(global, {
        factCheckResults: {
          ...results,
          [resultKey]: { error: errorMsg },
        },
      }, tabId);
    } else {
      global = updateTabState(global, {
        factCheckResults: {
          ...results,
          [resultKey]: { error: 'Fact-check failed' },
        },
      }, tabId);
    }
  } catch {
    global = getGlobal();
    const results = selectTabState(global, tabId).factCheckResults || {};
    global = updateTabState(global, {
      factCheckResults: {
        ...results,
        [resultKey]: { error: 'Network error' },
      },
    }, tabId);
  }

  setGlobal(global);
});

addActionHandler('dismissFactCheck', (global, _actions, payload): ActionReturnType => {
  const { chatId, messageId, tabId = getCurrentTabId() } = payload;
  const resultKey = `${chatId}_${messageId}`;
  const tabState = selectTabState(global, tabId);
  const currentResults = tabState.factCheckResults || {};
  const newResults = { ...currentResults };
  delete newResults[resultKey];
  return updateTabState(global, { factCheckResults: newResults }, tabId);
});

addActionHandler('openAiChatPanel', (global, _actions, payload): ActionReturnType => {
  const { tabId = getCurrentTabId() } = payload || {};
  const tabState = selectTabState(global, tabId);
  const selectedMessageIds = tabState.selectedMessages?.messageIds;
  const chatId = tabState.selectedMessages?.chatId;
  if (!chatId || !selectedMessageIds?.length) return undefined;

  const messagesById = global.messages.byChatId[chatId]?.byId;
  if (!messagesById) return undefined;

  const usersById = global.users.byId;
  const chatsById = global.chats.byId;
  const chatTitle = chatsById[chatId]?.title || 'Chat';

  // Sanitize user-controlled strings to prevent prompt injection
  function sanitizeName(raw: string): string {
    if (!raw) return '';
    return raw
      .replace(/[\n\r\t]/g, ' ') // no newlines (prompt injection vector)
      .replace(/[[\]{}()]/g, '') // no brackets (confuses structured prompts)
      .replace(/[^\p{L}\p{N}\p{Emoji}\s._-]/gu, '') // only letters, numbers, emoji, spaces, dots, dashes
      .trim()
      .slice(0, 64); // max 64 chars
  }

  // Build current user identity
  const currentUserId = global.currentUserId || '';
  const currentUserObj = currentUserId ? usersById[currentUserId] : undefined;
  const currentUserName = currentUserObj
    ? sanitizeName([currentUserObj.firstName, currentUserObj.lastName].filter(Boolean).join(' '))
    : 'Me';

  const selectedMessages = selectedMessageIds
    .map((id: number) => messagesById[id])
    .filter(Boolean)
    .sort((a: any, b: any) => (a.date || 0) - (b.date || 0))
    .filter((msg: any) => msg.content.text?.text) // skip media-only messages
    .map((msg: any) => {
      // isOutgoing = sent by current user
      const isMyMessage = Boolean(msg.isOutgoing);

      if (isMyMessage) {
        return { author: `${currentUserName || 'Me'} #${currentUserId}`, text: msg.content.text?.text || '' };
      }

      // For incoming messages, resolve sender
      const sid = msg.senderId || chatId; // fallback to chatId in private chats
      const user = usersById[sid];
      const chat = chatsById[sid];
      const rawName = user
        ? [user.firstName, user.lastName].filter(Boolean).join(' ')
        : (chat?.title || chatTitle);
      const safeName = sanitizeName(rawName) || `User_${sid || chatId}`;
      return { author: `${safeName} #${sid || chatId}`, text: msg.content.text?.text || '' };
    });

  return updateTabState(global, {
    aiPanel: { type: 'askAi' as const, isLoading: false, chatId, selectedMessages },
  }, tabId);
});

addActionHandler('requestAskAi', async (global, actions, payload): Promise<void> => {
  const { question, tabId = getCurrentTabId() } = payload;
  const { aiApiKey, aiProvider } = selectSharedSettings(global);
  const aiBackendUrl = aiBackendBase();

  if (aiProvider !== 'claude-cli' && !aiApiKey) {
    actions.showNotification({ message: { key: 'AiNoApiKey' }, tabId });
    return;
  }

  const tabState = selectTabState(global, tabId);
  const panel = tabState.aiPanel;
  if (!panel || panel.type !== 'askAi') return;

  // Identify current user by #ID (same format as message authors)
  const curUserId = global.currentUserId || '';
  const curUser = curUserId ? global.users.byId[curUserId] : undefined;
  const curName = curUser
    ? [curUser.firstName, curUser.lastName].filter(Boolean).join(' ')
    : 'User';
  const questionWithContext = `[I am ${curName} #${curUserId}. Match authors by #ID numbers.]\n\n${question}`;

  global = updateTabState(global, {
    aiPanel: { ...panel, isLoading: true, question },
  }, tabId);
  setGlobal(global);

  try {
    const askHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Provider': aiProvider,
      ...aiAuthHeaders(),
    };
    if (aiApiKey) {
      askHeaders.Authorization = `Bearer ${aiApiKey}`;
    }

    const response = await fetch(`${aiBackendUrl}/api/ask/stream`, {
      method: 'POST',
      headers: askHeaders,
      body: JSON.stringify({ messages: panel.selectedMessages, question: questionWithContext }),
    });
    handleAiUnauthorized(response.status);

    if (!response.ok || !response.body) {
      let errorMessage = response.status === 429
        ? 'Free limit reached (3 Ask AI / week). Upgrade to Pro for more.'
        : response.status === 401
          ? 'Connect your account in AI settings to use Ask AI.'
          : 'Request failed';
      try {
        const errData = await response.json();
        const detail = errData?.detail ?? errData;
        if (detail?.message) errorMessage = detail.message;
      } catch { /* keep the status-based default */ }
      global = getGlobal();
      global = updateTabState(global, {
        aiPanel: { ...panel, isLoading: false, question, error: errorMessage },
      }, tabId);
      setGlobal(global);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullAnswer = '';
    let streamDone = false;
    let artifactData: { filename: string; title: string; content_type: string; data_b64: string } | undefined;
    let currentEventType = '';

    while (!streamDone) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEventType = line.slice(7).trim();
          continue;
        }
        if (!line.startsWith('data: ')) continue;
        try {
          const data = JSON.parse(line.slice(6));

          // Handle artifact SSE event
          if (currentEventType === 'artifact') {
            artifactData = data;
            currentEventType = '';
            continue;
          }
          currentEventType = '';

          if (data.done) {
            streamDone = true;
            break;
          }
          if (data.text) {
            fullAnswer += data.text;
            global = getGlobal();
            const currentPanel = selectTabState(global, tabId).aiPanel;
            if (currentPanel) {
              global = updateTabState(global, {
                aiPanel: {
                  type: 'askAi' as const,
                  isLoading: true,
                  chatId: currentPanel.chatId,
                  selectedMessages: currentPanel.selectedMessages,
                  question: currentPanel.question,
                  answer: fullAnswer,
                  chatMessage: fullAnswer,
                },
              }, tabId);
              setGlobal(global);
            }
          }
        } catch {
          currentEventType = '';
        }
      }
    }

    // Decode artifact binary if received
    let artifactBinary: ArrayBuffer | undefined;
    if (artifactData?.data_b64) {
      const binaryStr = atob(artifactData.data_b64);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
      }
      artifactBinary = bytes.buffer;
    }

    // Final update — set isLoading false, answer + artifact
    global = getGlobal();
    const finalPanel = selectTabState(global, tabId).aiPanel;
    if (finalPanel) {
      global = updateTabState(global, {
        aiPanel: {
          type: 'askAi' as const,
          isLoading: false,
          chatId: finalPanel.chatId,
          selectedMessages: finalPanel.selectedMessages,
          question: finalPanel.question,
          answer: fullAnswer,
          chatMessage: fullAnswer,
          artifact: artifactBinary ? {
            filename: artifactData!.filename,
            title: artifactData!.title,
            contentType: artifactData!.content_type,
            binary: artifactBinary,
          } : undefined,
        },
      }, tabId);
      setGlobal(global);
    }
  } catch {
    global = getGlobal();
    global = updateTabState(global, {
      aiPanel: {
        ...(selectTabState(global, tabId).aiPanel || panel), isLoading: false, question, error: 'Network error',
      },
    }, tabId);
    setGlobal(global);
  }
});

// Music Player Actions

// Old audio singleton removed — YouTube now plays through native AudioPlayer

// Monotonic id for synthetic YouTube-track messages so same-millisecond plays can't collide.
let lastMusicMessageId = 0;
// Bounded window of recent synthetic YouTube messages kept in global state (player needs to resolve
// the current + just-previous track; the rest are pruned so it can't grow unbounded).
const MAX_SYNTHETIC_TRACKS = 20;

const DEFAULT_MUSIC_PLAYER = {
  isPlaying: false,
  queue: [] as Array<{
    videoId: string; title: string; channel: string; thumbnail: string; duration: number;
  }>,
  currentTime: 0,
  volume: 1,
  isExpanded: false,
};

addActionHandler('loadSavedAudio', async (global, _actions, payload): Promise<void> => {
  const { tabId = getCurrentTabId() } = payload || {};
  const userId = global.currentUserId;
  if (!userId) return;

  const peer = selectPeer(global, userId);
  if (!peer) return;

  // Set loading state
  global = getGlobal();
  const mp = selectTabState(global, tabId).musicPlayer || DEFAULT_MUSIC_PLAYER;
  global = updateTabState(global, {
    musicPlayer: { ...mp, isSavedLoading: true },
  }, tabId);
  setGlobal(global);

  try {
    const result = await callApi('searchMessagesInChat', {
      peer,
      type: 'audio' as ApiMessageSearchType,
      limit: 50,
      threadId: MAIN_THREAD_ID,
    });

    global = getGlobal();
    const currentMp = selectTabState(global, tabId).musicPlayer || DEFAULT_MUSIC_PLAYER;

    if (!result || !result.messages.length) {
      global = updateTabState(global, {
        musicPlayer: { ...currentMp, isSavedLoading: false, savedTracks: [] },
      }, tabId);
      setGlobal(global);
      return;
    }

    // Add messages to global store so AudioPlayer can find them
    const byId = buildCollectionByKey(result.messages, 'id');
    global = addChatMessagesById(global, userId, byId);

    // Extract audio track info
    const savedTracks = result.messages
      .filter((m) => m.content?.audio)
      .map((m) => {
        const audio = m.content.audio!;
        return {
          id: m.id,
          chatId: userId,
          date: m.date,
          title: audio.title || audio.fileName || 'Audio',
          performer: audio.performer || '',
          duration: audio.duration || 0,
          size: audio.size || 0,
          mediaDocId: audio.id,
          thumbnailDocId: audio.thumbnailSizes?.length ? audio.id : undefined,
        };
      });

    global = updateTabState(global, {
      musicPlayer: { ...currentMp, isSavedLoading: false, savedTracks },
    }, tabId);
    setGlobal(global);
  } catch {
    // Never leave the "My Music" tab spinning if the audio fetch fails.
    global = getGlobal();
    const failMp = selectTabState(global, tabId).musicPlayer || DEFAULT_MUSIC_PLAYER;
    global = updateTabState(global, {
      musicPlayer: { ...failMp, isSavedLoading: false },
    }, tabId);
    setGlobal(global);
  }
});

addActionHandler('musicPlay', async (global, actions, payload): Promise<void> => {
  const { videoId, skipHistory, tabId = getCurrentTabId() } = payload;
  const backendUrl = aiBackendBase();

  // Loading state: the clicked row shows a spinner while yt-dlp extracts the stream (can take
  // 5–15s on a cold cache) — without it the click looks dead.
  global = getGlobal();
  const existingPlayer = selectTabState(global, tabId).musicPlayer;
  global = updateTabState(global, {
    musicPlayer: {
      ...(existingPlayer || DEFAULT_MUSIC_PLAYER),
      isPlaying: false,
      loadingVideoId: videoId,
      currentTime: 0,
      currentTrack: existingPlayer?.currentTrack,
    },
  }, tabId);
  setGlobal(global);

  const clearLoading = () => {
    global = getGlobal();
    const mpNow = selectTabState(global, tabId).musicPlayer;
    if (mpNow?.loadingVideoId !== videoId) return;
    global = updateTabState(global, {
      musicPlayer: { ...mpNow, loadingVideoId: undefined },
    }, tabId);
    setGlobal(global);
  };

  const abort = new AbortController();
  const fetchTimeout = window.setTimeout(() => abort.abort(), 45000);

  try {
    global = getGlobal();
    const response = await fetch(`${backendUrl}/api/music/stream?video_id=${encodeURIComponent(videoId)}`, {
      headers: aiAuthHeaders(),
      signal: abort.signal,
    });

    if (!response.ok) {
      handleAiUnauthorized(response.status);
      clearLoading();
      actions.showNotification({ message: 'Failed to load track', tabId });
      return;
    }

    const data = await response.json();
    // Stream URL is network-controlled — only ever feed an http(s) URL to the audio element.
    if (!data.url || !/^https?:\/\//i.test(String(data.url))) {
      clearLoading();
      actions.showNotification({ message: 'Failed to load track', tabId });
      return;
    }

    // Synthetic message ID — monotonic (a plain Date.now() collides on same-ms double-plays).
    lastMusicMessageId = Math.max(Date.now(), lastMusicMessageId + 1);
    const messageId = lastMusicMessageId;
    const date = Math.floor(Date.now() / 1000);

    // Inject synthetic message into global state. Keep a BOUNDED WINDOW of recent tracks rather
    // than replacing with only the current one: openAudioPlayer runs AFTER this setGlobal, so a
    // re-render in between still references the previous messageId — dropping it made the player
    // bar flicker/disappear. A small cap prevents the unbounded growth the merge used to cause.
    global = getGlobal();
    const prevById = global.messages.byChatId[YOUTUBE_CHAT_ID]?.byId || {};
    const keptIds = Object.keys(prevById)
      .sort((a, b) => Number(b) - Number(a))
      .slice(0, MAX_SYNTHETIC_TRACKS - 1);
    const boundedById: Record<number, ApiMessage> = {};
    for (const id of keptIds) boundedById[Number(id)] = prevById[Number(id)];
    global = {
      ...global,
      messages: {
        ...global.messages,
        byChatId: {
          ...global.messages.byChatId,
          [YOUTUBE_CHAT_ID]: {
            byId: {
              ...boundedById,
              [messageId]: {
                id: messageId,
                chatId: YOUTUBE_CHAT_ID,
                date,
                isOutgoing: false,
                senderId: YOUTUBE_CHAT_ID,
                content: {
                  audio: {
                    mediaType: 'audio' as const,
                    id: videoId,
                    size: 0,
                    mimeType: 'audio/webm',
                    fileName: `${data.title || videoId}.webm`,
                    duration: data.duration || 0,
                    performer: data.channel || '',
                    title: data.title || videoId,
                  },
                },
              } as ApiMessage,
            },
            summaryById: {},
            threadsById: {},
          },
        },
      },
    };

    // Update musicPlayer state for YouTubeSearch UI
    const mp = selectTabState(global, tabId).musicPlayer;
    global = updateTabState(global, {
      musicPlayer: {
        ...(mp || DEFAULT_MUSIC_PLAYER),
        isPlaying: true,
        loadingVideoId: undefined,
        currentTime: 0,
        currentTrack: {
          videoId,
          title: data.title || '',
          channel: data.channel || '',
          thumbnail: data.thumbnail || '',
          duration: data.duration || 0,
          streamUrl: data.url,
        },
      },
    }, tabId);
    setGlobal(global);

    // Save to YouTube play history (skip if playing from history list)
    if (!skipHistory) {
      try {
        const historyKey = 'youtube_play_history';
        const maxHistory = 50;
        const historyRaw = localStorage.getItem(historyKey);
        const history = historyRaw ? JSON.parse(historyRaw) : [];
        const trackData = {
          video_id: videoId,
          title: data.title || '',
          channel: data.channel || '',
          thumbnail: data.thumbnail || '',
          duration: data.duration || 0,
        };
        const filtered = history.filter((t: { video_id: string }) => t.video_id !== videoId);
        const updated = [trackData, ...filtered].slice(0, maxHistory);
        localStorage.setItem(historyKey, JSON.stringify(updated));
      } catch { /* ignore */ }
    }

    // Save trackId → videoId mapping for UI (YouTubeSearch needs videoId to highlight playing track)
    try {
      localStorage.setItem(`yt_track_${messageId}`, videoId);
    } catch { /* ignore */ }

    // Play through OUR backend proxy, NOT the raw googlevideo URL: browsers in geo-restricted
    // networks (RU) can't fetch googlevideo directly (MediaError SRC_NOT_SUPPORTED), but the backend
    // can and streams it same-origin with Range support.
    const trackId = `msg${YOUTUBE_CHAT_ID}-${messageId}-${date}` as TrackId;
    const playbackSrc = `${backendUrl}/api/music/audio?video_id=${encodeURIComponent(videoId)}`;
    audioController.registerTrack(trackId, 'audio');
    audioController.setTrackSrc(trackId, playbackSrc);

    // openAudioPlayer first — AudioPlayer subscribes to trackId, then autoplay triggers play
    getActions().openAudioPlayer({
      chatId: YOUTUBE_CHAT_ID,
      messageId,
      origin: AudioOrigin.Search,
      tabId,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[musicPlay] Error:', err);
    clearLoading();
    actions.showNotification({ message: 'Music playback error', tabId });
  } finally {
    window.clearTimeout(fetchTimeout);
  }
});

// Note: search is performed by the panel directly (fetchMusicSearch) — there is no musicSearch
// action; playback control (pause/resume/seek/volume) lives in audioController, not in actions.
